import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Building2, ChevronDown, ImagePlus, Trash2, X } from "lucide-react";
import ProjectTreeImageEditor from "components/project/ProjectTreeImageEditor";
import ProjectTreeOverview, { ProjectTreeBranchSummary } from "./ProjectTreeOverview";
import TreeNodeDashboard from "./TreeNodeDashboard";
import { useAppStore } from "lib/store/useAppStore";
import { getProjectNodes, postProjectNodeImage, deleteProjectNodeImage } from "lib/api/projectStructure";
import { projectNodePaths } from "lib/utils/projectNodePaths";
import { buildTreeMetrics, DEFAULT_TREE_METRICS, DEFAULT_TREE_CHART_STYLES, getTreeNodeMetrics, readTreeMetrics, readTreeChartStyles, TREE_CHARTS, TREE_NODE_COUNT_METRIC_IDS } from "lib/utils/projectTreeMetrics";
import "css/projectTreeDashboard.css";

const LEVELS = [["treeBuildingName", "building_name", "building"], ["treeFloor", "floor", "floor"], ["treeLocation", "location", "location"], ["treeFacilityName", "facility_name", "facility"], ["treePartName", "part_name", "part"]];
const SCOPE_KEYS = [...LEVELS.map(([key]) => key), "treeFacilityType", "treeNodeId", "category", "index", "value"];

function TreePreviewDialog({ title, children, onClose }) {
  const dialogRef = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = dialogRef.current, previous = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); previous?.focus?.(); };
  }, []);
  return <dialog ref={dialogRef} className="treeDashboardImagePreview treeDashboardTreePreview" aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); onClose(); }}>
    <div className="treeDashboardImagePreview__toolbar">
      <h3 id={titleId}>{title}</h3>
      <button type="button" className="treeDashboardImagePreview__close" aria-label="현장 경로 확대 보기 닫기" title="닫기" onClick={onClose}><X size={18} aria-hidden="true" /></button>
    </div>
    <div className="treeDashboardTreePreview__body">{children}</div>
  </dialog>;
}

function ImagePreviewDialog({ node, onChangeImage, onDeleteImage, deleting, notice, onClose }) {
  const dialogRef = useRef(null);
  const titleId = useId();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const dialog = dialogRef.current, previous = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); previous?.focus?.(); };
  }, []);
  return <dialog ref={dialogRef} className="treeDashboardImagePreview" aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); if (!deleting) onClose(); }}>
    <div className="treeDashboardImagePreview__toolbar">
      <h3 id={titleId}>{node.name} 대표 이미지</h3>
      <div className="treeDashboardImagePreview__actions">
        <button type="button" disabled={deleting} aria-label="사진 변경" title="사진 변경" onClick={onChangeImage}><ImagePlus size={16} aria-hidden="true" /><span>사진 변경</span></button>
        <button type="button" disabled={deleting || !node.image_path} aria-label="사진 삭제" title="사진 삭제" onClick={onDeleteImage}><Trash2 size={16} aria-hidden="true" /><span>{deleting ? "삭제 중…" : "사진 삭제"}</span></button>
      </div>
      <button type="button" className="treeDashboardImagePreview__close" disabled={deleting} aria-label="사진 미리보기 닫기" title="닫기" onClick={onClose}><X size={18} aria-hidden="true" /></button>
    </div>
    {notice && <p className="treeDashboardImagePreview__notice" role="status">{notice}</p>}
    <div className="treeDashboardImagePreview__body">
      {failed ? <p role="alert">사진을 불러오지 못했습니다. 창을 닫고 다시 확인해 주세요.</p>
        : <img src={node.image_url} alt={`${node.name} 대표 이미지 확대`} onError={() => setFailed(true)} />}
    </div>
  </dialog>;
}

function ImageUploadDialog({ node, onSave, onClose }) {
  const dialogRef = useRef(null);
  const titleId = useId();
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (file) return;
    const dialog = dialogRef.current, previous = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); previous?.focus?.(); };
  }, [file]);
  if (file) return <ProjectTreeImageEditor file={file} nodeName={node.name} onSave={image => onSave(node, image)} onClose={onClose} />;
  return <dialog ref={dialogRef} className="treeDashboardSettings" aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); onClose(); }}>
    <div className="treeDashboard__heading"><h3 id={titleId}>대표 이미지 등록</h3><button type="button" aria-label="등록 닫기" onClick={onClose}><X size={18} /></button></div>
    <p>{node.name} · 사진을 선택한 뒤 크기와 위치를 조절하고 저장하세요.</p>
    <label className="treeDashboardImageUpload">사진 선택 (JPG, PNG, WebP, GIF · 최대 10MB)
      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={event => {
        const next = event.target.files?.[0]; event.target.value = "";
        if (!next) return;
        if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(next.type)) { setError("JPG, PNG, WebP, GIF 이미지를 선택해 주세요."); return; }
        if (next.size > 10 * 1024 * 1024) { setError("10MB 이하의 이미지를 선택해 주세요."); return; }
        setError(""); setFile(next);
      }} />
    </label>
    {error && <p role="alert">{error}</p>}
  </dialog>;
}

function MetricSettings({ selected, options, chartStyles, onSave, onClose }) {
  const dialogRef = useRef(null);
  const validIds = new Set([...options, ...TREE_CHARTS].map(([id]) => id));
  const [draft, setDraft] = useState(() => selected.filter(id => validIds.has(id)));
  const [styles, setStyles] = useState(chartStyles);
  const toggle = (id, checked) => setDraft(value => checked ? [...value, id] : value.filter(item => item !== id));
  useEffect(() => {
    const dialog = dialogRef.current, previous = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); previous?.focus?.(); };
  }, []);
  return <dialog ref={dialogRef} className="treeDashboardSettings treeDashboardSettings--metrics" aria-labelledby="treeDashboardSettingsTitle"
    onCancel={event => { event.preventDefault(); onClose(); }}>
    <div className="treeDashboard__heading"><h3 id="treeDashboardSettingsTitle">표시할 지표 설정</h3><button type="button" aria-label="설정 닫기" onClick={onClose}><X size={18} /></button></div>
    <p>현재 선택 범위에 적용되며, 이 브라우저에 저장됩니다.</p>
    {[
      ["파일 · 진단 결과", ["total", "image", "video", "normal", "caution", "abnormal", "recheck"]],
      ["설비", ["electrical", "gas", "mechanical", "facilities"]],
      ["하위 계층 구성", TREE_NODE_COUNT_METRIC_IDS],
      ["진단 비율", ["normalRate", "cautionRate", "abnormalRate"]],
    ].map(([label, ids]) => {
      const groupOptions = options.filter(([id]) => ids.includes(id));
      return groupOptions.length ? <fieldset key={label}><legend>{label}</legend>
        <div>{groupOptions.map(([id, name]) => <label key={id}><input type="checkbox" checked={draft.includes(id)} onChange={event => toggle(id, event.target.checked)} />{name}</label>)}</div>
      </fieldset> : null;
    })}
    <fieldset className="treeDashboardSettings__graphs"><legend>그래프</legend>
      {TREE_CHARTS.map(([id, name]) => <div className="treeDashboardSettings__graphRow" key={id}>
        <label><input type="checkbox" checked={draft.includes(id)} onChange={event => toggle(id, event.target.checked)} />{name}</label>
        <select aria-label={`${name} 표시 방식`} value={styles[id]} disabled={!draft.includes(id)} onChange={event => setStyles(value => ({ ...value, [id]: event.target.value }))}>
          <option value="donut">원형 그래프</option><option value="bar">막대 그래프</option>
        </select>
      </div>)}
    </fieldset>
    {!draft.length && <p role="status">지표 또는 그래프를 하나 이상 선택하세요.</p>}
    <div className="treeDashboardSettings__actions"><button type="button" onClick={() => { setDraft(DEFAULT_TREE_METRICS); setStyles({ ...DEFAULT_TREE_CHART_STYLES }); }}>기본값</button>
      <button type="button" onClick={onClose}>취소</button><button type="button" className="is-primary" disabled={!draft.length} onClick={() => onSave(draft, styles)}>적용</button></div>
  </dialog>;
}

const CHART_COLORS = {
  resultChart: ["#4474c4", "#b78320", "#c85b62", "#8265aa", "#94a3b8"],
  fileTypeChart: ["#4474c4", "#429b9e", "#94a3b8"],
  typeChart: ["#4474c4", "#429b9e", "#8265aa", "#b78320", "#94a3b8"],
};
const nodeCountMetricIds = new Set(TREE_NODE_COUNT_METRIC_IDS);
const dashboardMetricLabel = label => label.replace(/ 수$/, "");
function MetricChart({ id, title, values, mode }) {
  const total = values.reduce((sum, [, value]) => sum + value, 0);
  const unit = id === "typeChart" ? "설비" : "파일";
  const colors = CHART_COLORS[id];
  let offset = 0;
  const gradient = values.flatMap(([, value], index) => {
    if (!value || !total) return [];
    const start = offset; offset += value / total * 100;
    return [`${colors[index]} ${start}% ${offset}%`];
  }).join(", ");
  return <section className="treeDashboard__chart" aria-label={title}>
    <header><h4>{title}</h4><span>{unit} {total.toLocaleString()}개</span></header>
    {total ? <div className={`treeDashboard__chartBody is-${mode}`}>
      {mode === "donut" && <div className="treeDashboard__donut" style={{ background: `conic-gradient(${gradient})` }} aria-hidden="true"><div><strong>{total.toLocaleString()}</strong><span>{unit}</span></div></div>}
      <ul className="treeDashboard__legend">{values.map(([label, value], index) => <li key={label}>
        <div><span className="treeDashboard__legendLabel"><i style={{ backgroundColor: colors[index] }} aria-hidden="true" />{label}</span><strong>{value.toLocaleString()}<small>개 · {Math.round(value / total * 100)}%</small></strong></div>
        {mode === "bar" && <div className="treeDashboard__track" aria-hidden="true"><span style={{ width: `${value / total * 100}%`, backgroundColor: colors[index] }} /></div>}
      </li>)}</ul>
    </div> : <p className="treeDashboard__chartEmpty">집계할 {unit}가 없습니다.</p>}
  </section>;
}

function DashboardContent({ storageKey, images, uploadTarget, onUploadImage, onDeleteImage, nodesLoading, nodesError, nodeMetrics, files, loading, error, onRetry, root, treeOverview, branchSummary, settingsOpen, onSettingsClose, nodeContent, nodeTitle }) {
  const [selected, setSelected] = useState(() => readTreeMetrics(storageKey));
  const [chartStyles, setChartStyles] = useState(() => readTreeChartStyles(storageKey));
  const [notice, setNotice] = useState("");
  const [imageTarget, setImageTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [treePreview, setTreePreview] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);
  const removeImage = async () => {
    if (deletingRef.current || !previewTarget?.image_path) return;
    if (!window.confirm(`${previewTarget.name} 대표 이미지를 삭제할까요? 실제 이미지 파일이 삭제되며 복구할 수 없습니다. 등록된 경로와 업로드 파일은 유지됩니다.`)) return;
    deletingRef.current = true; setDeleting(true); setNotice("");
    try {
      const result = await onDeleteImage(previewTarget);
      setNotice(result.message || (result.success ? "대표 이미지를 삭제했습니다." : "삭제하지 못했습니다."));
      if (result.success) setPreviewTarget(null);
    }
    catch { setNotice("삭제 결과를 확인하지 못했습니다. 목록을 다시 조회하세요."); }
    finally { deletingRef.current = false; setDeleting(false); }
  };
  const chartId = useId();
  const [chartsOpen, setChartsOpen] = useState(() => {
    try { return localStorage.getItem(`${storageKey}:charts-open`) === "true"; }
    catch { return false; }
  });
  const metrics = useMemo(() => buildTreeMetrics(files), [files]);
  const hasCharts = !nodeContent && TREE_CHARTS.some(([id]) => selected.includes(id));
  const charts = !error && !loading && <div className="treeDashboard__charts">{TREE_CHARTS.filter(([id]) => selected.includes(id)).map(([id, title]) => <MetricChart key={id} id={id} title={title} values={metrics[id]} mode={chartStyles[id]} />)}</div>;
  const heading = <div className={`treeDashboard__heading treeDashboard__heading--summary ${root ? "is-root" : ""} ${branchSummary ? "has-branch-summary" : ""}`}><div><h3>{nodeTitle || (branchSummary ? "대표 이미지" : root ? "프로젝트 현황" : "선택 범위 현황")}</h3></div>
      {!nodeContent && <div className="treeDashboard__summaryGroup">
      <div className="treeDashboard__dataHeading"><h4>요약</h4></div>
      {branchSummary && <div className="treeDashboard__dataHeading treeDashboard__branchHeading"><h4>하위 경로</h4></div>}
      </div>}
    </div>;
  const pictures = <div className="treeDashboard__pictures">
    {images.length ? <div className={`treeDashboard__collage ${images.length === 1 ? "is-single" : ""}`}>{images.slice(0, 4).map(node => <figure key={node.id}>
      <button type="button" className="treeDashboard__imagePreviewButton" disabled={deleting} aria-label={`${node.name} 대표 이미지 크게 보기`} onClick={() => { setNotice(""); setPreviewTarget(node); }}>
        <img src={node.image_url} alt={`${node.name} 대표 이미지`} loading="lazy" />
      </button></figure>)}</div>
      : uploadTarget && !nodesLoading && !nodesError ? <button type="button" className="treeDashboard__placeholder is-upload" onClick={() => setImageTarget(uploadTarget)} aria-label={`${uploadTarget.name} 대표 이미지 등록`}>
        <ImagePlus size={30} aria-hidden="true" /><span>대표 이미지 등록</span><small>클릭하여 사진을 선택하세요.</small>
      </button> : <div className="treeDashboard__placeholder"><Building2 size={30} aria-hidden="true" /><span>{nodesLoading ? "대표 이미지 불러오는 중…" : nodesError ? "대표 이미지를 불러오지 못했습니다." : "등록된 대표 이미지가 없습니다."}</span><small>건물·층·위치·설비·부품 중 하나를 선택해 대표 이미지를 등록하세요.</small></div>}
  </div>;
  const identity = <div className="treeDashboard__identity">{heading}{pictures}</div>;
  const openTreePreview = (event, kind) => {
    if (event.target.closest("a, button, input, select, textarea, [contenteditable='true']")) return;
    event.currentTarget.focus({ preventScroll: true });
    setTreePreview(kind);
  };
  const treeFrame = (kind, content) => <div className={`treeDashboard__treeFrame is-${kind}`} tabIndex={0}
    aria-label={`${kind === "root" ? "프로젝트 전체" : "선택 범위"} 경로. 빈 공간을 클릭하거나 Enter 키를 누르면 크게 볼 수 있습니다.`}
    onClick={event => openTreePreview(event, kind)} onKeyDown={event => {
      if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return;
      event.preventDefault(); setTreePreview(kind);
    }}>
    {content}
  </div>;
  return <div className={`treeDashboard ${nodeContent ? "treeDashboard--node" : ""}`} aria-busy={nodeContent ? nodesLoading : loading}>
    {!nodeContent && heading}
    <div className={`treeDashboard__summary ${root ? "is-root" : ""} ${branchSummary ? "has-branch-summary" : ""}`}>
      {nodeContent ? nodeContent(identity) : <>
      {root ? treeFrame("root", treeOverview) : pictures}
      <div className="treeDashboard__summaryGroup"><div className="treeDashboard__data">
        {error ? <div role="alert"><p>{error}</p><button type="button" onClick={onRetry}>다시 불러오기</button></div>
          : loading ? <p role="status">선택 범위의 지표를 불러오는 중…</p> : <>
            <dl className="treeDashboard__kpis">{nodeMetrics.options.filter(([id]) => selected.includes(id)).map(([id, label]) => {
              const value = id in nodeMetrics.counts ? (nodesLoading || nodesError ? "—" : nodeMetrics.counts[id]) : metrics[id];
              const displayValue = typeof value === "number" ? `${value.toLocaleString()}${nodeCountMetricIds.has(id) ? "개" : ""}` : value;
              return <div key={id}><dt>{dashboardMetricLabel(label)}</dt><dd>{displayValue}</dd></div>;
            })}</dl>
          </>}
      </div>
      {branchSummary && treeFrame("branch", branchSummary)}
      </div></>}
    </div>
    {hasCharts && <>
      <button type="button" className="treeDashboard__expand" aria-expanded={chartsOpen} aria-controls={chartId} onClick={() => {
        const next = !chartsOpen; setChartsOpen(next);
        try { localStorage.setItem(`${storageKey}:charts-open`, String(next)); }
        catch { setNotice("현재 화면에만 적용했습니다. 브라우저에서 펼침 상태를 저장할 수 없습니다."); }
      }}>{chartsOpen ? "상세 내용 접기" : "자세히 보기"}<ChevronDown size={16} aria-hidden="true" /></button>
      <div id={chartId} hidden={!chartsOpen}>
        {chartsOpen && charts}
      </div>
    </>}
    {notice && <p role="status">{notice}</p>}
    {previewTarget && <ImagePreviewDialog node={previewTarget} deleting={deleting} notice={notice} onDeleteImage={removeImage} onClose={() => setPreviewTarget(null)}
      onChangeImage={() => { setImageTarget(previewTarget); setPreviewTarget(null); }} />}
    {treePreview && <TreePreviewDialog title={treePreview === "root" ? "프로젝트 전체 경로" : "선택한 범위의 경로"} onClose={() => setTreePreview(null)}>
      {treePreview === "root" ? treeOverview : branchSummary}
    </TreePreviewDialog>}
    {imageTarget && <ImageUploadDialog node={imageTarget} onSave={onUploadImage} onClose={() => setImageTarget(null)} />}
    {settingsOpen && !nodeContent && <MetricSettings selected={selected} options={nodeMetrics.options} chartStyles={chartStyles} onClose={onSettingsClose} onSave={(values, styles) => {
      setSelected(values); setChartStyles(styles); onSettingsClose();
      try { localStorage.setItem(storageKey, JSON.stringify(values)); localStorage.setItem(`${storageKey}:chart-styles`, JSON.stringify(styles)); setNotice(""); }
      catch { setNotice("현재 화면에 적용했습니다. 브라우저 저장이 차단되어 다음 방문에는 유지되지 않을 수 있습니다."); }
    }} />}
  </div>;
}

export default function ProjectTreeDashboard({ projectId, project, listScope = {}, refreshVersion, ...props }) {
  const userId = useAppStore(state => state.user?.id);
  const treeProject = useAppStore(state => state.projectTree?.find(item => item.project_id === projectId));
  const [nodeData, setNodeData] = useState({ projectId: null, nodes: [], loading: true, error: false });
  useEffect(() => {
    const controller = new AbortController();
    // Keep the current tree visible during background refreshes after mutations.
    setNodeData(previous => previous.projectId === projectId
      ? previous
      : { projectId, nodes: [], loading: true, error: false });
    getProjectNodes(projectId, controller.signal).then(result => {
      if (!controller.signal.aborted) setNodeData({ projectId, nodes: result.success ? result.data?.nodes || [] : [], loading: false, error: !result.success });
    }).catch(() => { if (!controller.signal.aborted) setNodeData({ projectId, nodes: [], loading: false, error: true }); });
    return () => controller.abort();
  }, [projectId, refreshVersion]);
  const nodes = nodeData.projectId === projectId ? nodeData.nodes : [];
  const paths = projectNodePaths(nodes);
  const deepest = LEVELS.reduce((last, [key], index) => listScope[key] ? index : last, -1);
  const matchingNodes = nodes.filter(node => {
    if (listScope.treeNodeId) return node.id === listScope.treeNodeId;
    const path = paths.find(item => item.id === node.id);
    if (!path) return false;
    if (deepest < 0) return !listScope.category;
    return node.node_type === LEVELS[deepest][2] && LEVELS.slice(0, deepest + 1).every(([key, field]) =>
      !listScope[key] || (listScope[key] === "__unassigned__" ? !path[field] : path[field] === listScope[key]));
  }).sort((a, b) => LEVELS.findIndex(item => item[2] === a.node_type) - LEVELS.findIndex(item => item[2] === b.node_type) || a.name.localeCompare(b.name, "ko"));
  const selectedNode = matchingNodes.length === 1 ? matchingNodes[0] : null;
  const specialized = ["facility", "part"].includes(selectedNode?.node_type);
  const nodeScope = specialized || deepest >= 3;
  const images = matchingNodes.filter(node => node.image_url);
  // Never guess the target when a name matches nodes under different parents.
  const uploadTarget = (deepest >= 0 || listScope.treeNodeId) && selectedNode && !listScope.category ? selectedNode : null;
  const uploadImage = async (node, file) => {
    if (file.size > 10 * 1024 * 1024) return { success: false, message: "저장할 이미지는 10MB 이하여야 합니다." };
    const result = await postProjectNodeImage(projectId, node.id, file);
    if (result.success) {
      const refreshed = Array.isArray(result.data?.nodes) ? result : await getProjectNodes(projectId);
      if (refreshed.success && Array.isArray(refreshed.data?.nodes)) {
        setNodeData(previous => previous.projectId === projectId ? { projectId, nodes: refreshed.data.nodes, loading: false, error: false } : previous);
      } else {
        return { success: false, message: "이미지는 저장됐지만 목록 갱신에 실패했습니다. 페이지를 다시 조회해 주세요." };
      }
    }
    return result;
  };
  const scope = Object.fromEntries(SCOPE_KEYS.filter(key => listScope[key]).map(key => [key, listScope[key]]));
  const removeImage = async node => {
    const result = await deleteProjectNodeImage(projectId, node.id, node.image_path);
    if (result.success && Array.isArray(result.data?.nodes)) {
      setNodeData(previous => previous.projectId === projectId ? { projectId, nodes: result.data.nodes, loading: false, error: false } : previous);
    }
    return result;
  };
  const storageKey = `project_tree_metrics_v1:${userId || "anonymous"}:${projectId}:${JSON.stringify(scope)}`;
  const root = !SCOPE_KEYS.some(key => listScope[key]);
  return <DashboardContent key={storageKey} storageKey={storageKey} images={images} root={root} uploadTarget={uploadTarget} onUploadImage={uploadImage} onDeleteImage={removeImage}
    nodeTitle={specialized ? `${selectedNode.name} · ${selectedNode.node_type === "facility" ? "부품별 진단 현황" : "측정 추이"}` : nodeScope ? "설비·부품 현황" : undefined}
    nodeContent={specialized ? identity => <TreeNodeDashboard key={selectedNode.id} identity={identity} projectId={projectId} node={selectedNode} nodes={nodes} refreshVersion={refreshVersion} /> : nodeScope
      ? identity => <div className="treeDashboard__summaryRow">{identity}<div className="treeDashboard__summaryGroup"><div className="nodeDashboard"><p role={nodeData.error ? "alert" : "status"} className="nodeDashboard__empty">{nodeData.loading ? "등록된 경로를 불러오는 중…" : nodeData.error ? "경로를 불러오지 못했습니다. 새로고침해 주세요." : "설비 또는 부품을 선택하세요. 경로가 미지정인 파일은 부품별 통계에서 제외됩니다."}</p></div></div></div> : null}
    nodeMetrics={getTreeNodeMetrics(nodes, listScope)}
    branchSummary={!nodeScope && deepest >= 0 && !listScope.treeFacilityType && !listScope.category
      ? <ProjectTreeBranchSummary projectId={projectId} nodes={nodes} files={treeProject?.files || props.files || []}
        listScope={listScope} loading={nodeData.loading} error={nodeData.error} /> : null}
    treeOverview={<ProjectTreeOverview projectId={projectId} projectName={project?.project_name} nodes={nodes}
      files={treeProject?.files || []} listScope={listScope} loading={nodeData.loading} error={nodeData.error} />}
    nodesLoading={nodeData.loading} nodesError={nodeData.error} {...props} />;
}
